import React from 'react';
import styled from 'styled-components';

import {getManagersList} from '../../../api';
import {createIdempotencyKey, setRentalManagerRole} from '../../../api/rental';
import Paper from '../../common/Paper';
import {
  ActionButton,
  Badge,
  ErrorText,
  Scroll,
  State,
  SuccessText,
  Table,
} from '../../Rental/styles';

export default function RentalRolesTab({authorization}) {
  const [managers, setManagers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const keys = React.useRef({});

  const load = React.useCallback(() => {
    setLoading(true);
    setError('');
    return getManagersList({authorization})
      .then(async response => {
        if (response.status < 200 || response.status >= 300) throw new Error('Не удалось загрузить менеджеров');
        return response.json();
      })
      .then(data => setManagers(Array.isArray(data) ? data : []))
      .catch(requestError => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [authorization]);

  React.useEffect(() => { load(); }, [load]);

  const assign = async manager => {
    if (!window.confirm(`Назначить ${manager.email || manager.id} менеджером проката? Эта роль не даёт доступа к зарядным и финансовым разделам.`)) return;
    setSavingId(manager.id);
    setError('');
    setMessage('');
    if (!keys.current[manager.id]) keys.current[manager.id] = createIdempotencyKey('rental-manager-role');
    try {
      await setRentalManagerRole(authorization, manager.id, keys.current[manager.id]);
      keys.current[manager.id] = '';
      setMessage(`Роль rental-manager назначена: ${manager.email || manager.id}.`);
      await load();
    } catch (requestError) {
      setError(`${requestError.message}. Повторная попытка будет отправлена с тем же ключом.`);
    } finally {
      setSavingId('');
    }
  };

  return (
    <Paper padding="24px">
      <Title>Роли проката</Title>
      <Description>
        `rental-manager` работает только с документами, автопарком и арендами. Назначение не открывает зарядные станции и финансовую статистику.
      </Description>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      {message && <SuccessText aria-live="polite">{message}</SuccessText>}
      {loading && <State>Загрузка менеджеров…</State>}
      {!loading && managers.length === 0 && <State>Менеджеры не найдены.</State>}
      {!loading && managers.length > 0 && (
        <Scroll>
          <Table minWidth="720px">
            <thead><tr><th>Менеджер</th><th>ID</th><th>Текущая роль</th><th>Действие</th></tr></thead>
            <tbody>
              {managers.map(manager => (
                <tr key={manager.id}>
                  <td>{[manager.first_name, manager.last_name].filter(Boolean).join(' ') || manager.email || '—'}</td>
                  <td>{manager.id}</td>
                  <td><Badge success={manager.role === 'rental-manager'}>{manager.role || 'manager'}</Badge></td>
                  <td>
                    <ActionButton
                      type="button"
                      primary
                      disabled={manager.role === 'rental-manager' || savingId === manager.id}
                      onClick={() => assign(manager)}
                    >
                      {savingId === manager.id ? 'Назначаем…' : manager.role === 'rental-manager' ? 'Уже назначена' : 'Назначить'}
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}
    </Paper>
  );
}

const Title = styled.h2`margin: 0 0 8px; font-size: 20px;`;
const Description = styled.p`margin: 0 0 20px; color: #666b73; line-height: 1.5;`;
